import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Typography, Chip, Collapse, IconButton, Button,
  MenuItem, Select, FormControl, useTheme, CircularProgress,
  ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { PlaybookRecommendation } from '../../types';
import { submitRecommendationOutcome } from '../../store/slices/playbooksSlice';

// ─── IOCHeader ───────────────────────────────────────────────────────────────

const reputationColor = (score: number | null, palette: any) => {
  if (score === null) return palette.text.secondary;
  if (score > 80) return palette.severity?.critical || palette.error.main;
  if (score >= 50) return palette.severity?.medium || palette.warning.main;
  return palette.severity?.low || palette.success.main;
};

const IOCHeader: React.FC<{ rec: PlaybookRecommendation }> = ({ rec }) => {
  const theme = useTheme();
  const scoreColor = reputationColor(rec.reputation_score, theme.palette);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Typography
        variant="body2"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 600,
          color: theme.palette.text.primary,
          wordBreak: 'break-all',
        }}
      >
        {rec.ioc_value || '—'}
      </Typography>

      {rec.ioc_type && (
        <Chip
          label={rec.ioc_type.toUpperCase()}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            fontWeight: 700,
            backgroundColor: theme.palette.background.tertiary,
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.border?.main || theme.palette.divider}`,
          }}
        />
      )}

      {rec.reputation_score !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.6rem', fontWeight: 700 }}>
            SCORE
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: scoreColor, fontFamily: '"JetBrains Mono", monospace' }}
          >
            {rec.reputation_score}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── ConfidenceChip ──────────────────────────────────────────────────────────

const confidenceConfig: Record<string, { label: string; color: 'error' | 'warning' | 'default' }> = {
  high: { label: 'HIGH', color: 'error' },
  medium: { label: 'MEDIUM', color: 'warning' },
  low: { label: 'LOW', color: 'default' },
  none: { label: 'NONE', color: 'default' },
};

const ConfidenceChip: React.FC<{ confidence: string | null }> = ({ confidence }) => {
  const cfg = confidence ? confidenceConfig[confidence] ?? confidenceConfig.none : confidenceConfig.none;
  return (
    <Chip
      label={`CONFIDENCE: ${cfg.label}`}
      color={cfg.color}
      size="small"
      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }}
    />
  );
};

// ─── RulesMatchedList ─────────────────────────────────────────────────────────

const RulesMatchedList: React.FC<{ rules: string[] }> = ({ rules }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  if (rules.length === 0) return null;

  return (
    <Box>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5,
          '&:hover': { color: theme.palette.primary?.main },
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.7rem' }}>
          RULES MATCHED ({rules.length})
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 1, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rules.map((rule) => (
            <Typography
              key={rule}
              variant="caption"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: theme.palette.text.secondary,
                fontSize: '0.7rem',
                backgroundColor: theme.palette.background.tertiary,
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                display: 'inline-block',
              }}
            >
              {rule}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── SuggestedActionsList ────────────────────────────────────────────────────

type Decision = 'accepted' | 'skipped' | null;

const SuggestedActionsList: React.FC<{
  actions: string[];
  decisions: Record<string, Decision>;
  onChange: (action: string, decision: Decision) => void;
  disabled?: boolean;
}> = ({ actions, decisions, onChange, disabled }) => {
  const theme = useTheme();

  if (actions.length === 0) return null;

  return (
    <Box>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
        SUGGESTED ACTIONS
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {actions.map((action) => (
          <Box key={action} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, color: theme.palette.text.primary, fontSize: '0.8rem' }}>
              {action}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={decisions[action] ?? null}
              onChange={(_, val) => onChange(action, val)}
              disabled={disabled}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.75, fontSize: '0.65rem', fontWeight: 600 } }}
            >
              <ToggleButton value="accepted" sx={{ color: 'success.main', '&.Mui-selected': { backgroundColor: 'success.main', color: '#fff' } }}>
                <Tooltip title="Accept"><CheckIcon sx={{ fontSize: 14 }} /></Tooltip>
              </ToggleButton>
              <ToggleButton value="skipped" sx={{ color: 'text.secondary', '&.Mui-selected': { backgroundColor: 'action.disabledBackground' } }}>
                <Tooltip title="Skip"><BlockIcon sx={{ fontSize: 14 }} /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── NoiseWarningBanner ───────────────────────────────────────────────────────

const NoiseWarningBanner: React.FC<{ warnings: string[] }> = ({ warnings }) => {
  const theme = useTheme();

  if (warnings.length === 0) return null;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.warning.main + '22',
        border: `1px solid ${theme.palette.warning.main}55`,
        borderRadius: 1,
        p: 1,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <WarningIcon sx={{ fontSize: 16, color: theme.palette.warning.main, mt: 0.1, flexShrink: 0 }} />
      <Box>
        {warnings.map((w, i) => (
          <Typography key={i} variant="caption" sx={{ display: 'block', color: theme.palette.warning.main, fontSize: '0.75rem' }}>
            {w}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

// ─── OutcomeForm ─────────────────────────────────────────────────────────────

const OUTCOME_OPTIONS = [
  { value: 'handled', label: 'Handled' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'false_positive', label: 'False Positive' },
];

const outcomeChipColor = (outcome: string) => {
  if (outcome === 'escalated') return 'error';
  if (outcome === 'false_positive') return 'warning';
  return 'default';
};

const OutcomeForm: React.FC<{
  rec: PlaybookRecommendation;
  decisions: Record<string, Decision>;
  onSubmitted: (updated: PlaybookRecommendation) => void;
}> = ({ rec, decisions, onSubmitted }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [outcome, setOutcome] = useState(rec.outcome || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reEvaluating, setReEvaluating] = useState(false);

  const isReadOnly = !!rec.outcome && !reEvaluating;

  const handleSubmit = async () => {
    if (!outcome) return;
    setSubmitting(true);
    setError(null);
    const feedback = Object.entries(decisions)
      .filter(([, d]) => d !== null)
      .map(([action, decision]) => ({ action, decision: decision! }));
    try {
      const result = await dispatch(
        submitRecommendationOutcome({ recommendationId: rec.id, outcome, actionFeedback: feedback }) as any
      ).unwrap();
      onSubmitted(result);
      setReEvaluating(false);
    } catch (e: any) {
      setError(e.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (isReadOnly) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 600, fontSize: '0.65rem' }}>
          OUTCOME
        </Typography>
        <Chip
          label={OUTCOME_OPTIONS.find(o => o.value === rec.outcome)?.label ?? rec.outcome}
          size="small"
          color={outcomeChipColor(rec.outcome!) as any}
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
        <Button
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={() => { setReEvaluating(true); setOutcome(rec.outcome || ''); }}
          sx={{ ml: 'auto', fontSize: '0.7rem', color: theme.palette.text.secondary }}
        >
          Re-evaluate
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          displayEmpty
          renderValue={(v) => v ? (OUTCOME_OPTIONS.find(o => o.value === v)?.label ?? v) : 'Select outcome…'}
          sx={{ fontSize: '0.8rem' }}
        >
          {OUTCOME_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        size="small"
        onClick={handleSubmit}
        disabled={!outcome || submitting}
        sx={{ fontSize: '0.75rem', minWidth: 80 }}
      >
        {submitting ? <CircularProgress size={14} /> : 'Submit'}
      </Button>

      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ─── Badges row ──────────────────────────────────────────────────────────────

const BadgesRow: React.FC<{ rec: PlaybookRecommendation }> = ({ rec }) => {
  const theme = useTheme();
  const items = [
    rec.country_code && { label: rec.country_code },
    rec.isp && { label: rec.isp },
    rec.usage_type && { label: rec.usage_type },
    rec.is_tor && { label: 'TOR', color: theme.palette.error.main },
    rec.is_malicious && { label: 'MALICIOUS', color: theme.palette.error.main },
  ].filter(Boolean) as { label: string; color?: string }[];

  if (items.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {items.map((item) => (
        <Chip
          key={item.label}
          label={item.label}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            fontWeight: 600,
            color: item.color ?? theme.palette.text.secondary,
            backgroundColor: item.color ? item.color + '22' : theme.palette.background.tertiary,
            border: `1px solid ${item.color ? item.color + '55' : (theme.palette.border?.main ?? theme.palette.divider)}`,
          }}
        />
      ))}
    </Box>
  );
};

// ─── RecommendationCard (main export) ────────────────────────────────────────

interface RecommendationCardProps {
  rec: PlaybookRecommendation;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({ rec: initialRec }) => {
  const theme = useTheme();
  const [rec, setRec] = useState(initialRec);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const handleDecisionChange = (action: string, decision: Decision) => {
    setDecisions((prev) => ({ ...prev, [action]: decision }));
  };

  const isReadOnly = !!rec.outcome;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border?.main ?? theme.palette.divider}`,
        borderRadius: 1.5,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.border?.light ?? theme.palette.divider}` }}>
        <IOCHeader rec={rec} />
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ConfidenceChip confidence={rec.confidence} />
          <BadgesRow rec={rec} />
        </Box>
      </Box>

      {/* Card body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <NoiseWarningBanner warnings={rec.noise_warnings} />

        <RulesMatchedList rules={rec.rules_matched} />

        <SuggestedActionsList
          actions={rec.suggested_actions}
          decisions={decisions}
          onChange={handleDecisionChange}
          disabled={isReadOnly}
        />

        <OutcomeForm rec={rec} decisions={decisions} onSubmitted={setRec} />
      </Box>
    </Box>
  );
};

export default RecommendationCard;
